import { VacancyApplication } from "../model/VacancyApplicant.interface";
import { APActivity } from "../../../ActivityInterfaces";
import {
  APOffer,
  APOfferStatus,
} from "../../../views/details/offer-feature/APOffer.Interface";
import { ActivityApplicationConstants } from "../../../ActivityHooks";
import { HTTP } from "@/utils/Http";
import { VACANCY_ACCORDION_LIST_PREFIX } from "./useVacancyActivity";
import DataBusDefaults from "@/services/DataBusDefaults";
import CommentsService from "@/modules/comments-module/CommentsService";
import { EmailUser } from "@/modules/comments-module/Comments";
import Log from "@/debug/Log";
import i18n from "@/i18n";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { VacancyMailSignature } from "../action-flow/hooks/useVacancyApplicantActivityFlow";
import ReactDOMServer from "react-dom/server";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import { AssetTypes } from "@/model/AssetTypes";
import CacheService from "@/services/CacheService";
import VacancyOfferUtils from "../vacancy-applicant/utils/VacancyOfferUtils";

type Props = {
  application: VacancyApplication;
  activity: APActivity;
  offer: APOffer;
  constants: ActivityApplicationConstants;
};

const useVacancyApplicantWorkflow = (props: Props) => {
  const { activity, application, constants, offer } = props;

  const rejectApplicantFunc = async () => {
    try {
      // 1. make request to change the offer
      const statusArchived: APOfferStatus = "archived";
      const statusOld: APOfferStatus = offer.data.status as APOfferStatus;

      await HTTP.post({
        url: `/${constants.serviceUrl}/${offer._id}/updateOffer`,
        target: "EMPTY",
        bodyParams: {
          status: statusArchived,
        },
      });

      // 2. after success send the email
      const emailUser: EmailUser = {
        address: application.data.applicants[0].email,
        name: `${application.data.applicants[0].firstName} ${application.data.applicants[0].lastName}`,
      };

      const rentalUnitName = activity.data.displayName;
      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
      })) as OAObject;

      const address = object.data.feature?.address?.address?.[0];
      const rentalUnitAddress = `${address?.streetname} ${address?.streetnumber}, ${address?.postalcode} ${address?.city}`;

      await CommentsService.sendMail({
        assetType: constants.assetType,
        assetId: activity._id,
        message: getDeclineEmailBody({
          application,
          rentalUnitName,
          rentalUnitAddress,
        }),
        subject: "Absage Mietanfrage",
        recipients: [emailUser],
      });

      // 3.1 after success show toast
      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Reject.Success",
          "Die Absage wurde dem Mietinteressenten gesendet."
        ),
      });

      // 3.2 reload list
      const rejectedIdentifier = `${VACANCY_ACCORDION_LIST_PREFIX}-${statusArchived}`;
      const oldIdentifier = `${VACANCY_ACCORDION_LIST_PREFIX}-${statusOld}`;

      DataBusDefaults.reload({
        identifiers: [rejectedIdentifier, oldIdentifier],
      });
    } catch (error) {
      // SHOW ERROR TOAST
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Reject.Error",
          "Beim Ablehnen des Mietinteressenten ist ein Fehler aufgetreten."
        ),
      });
      // LOG ERROR
      Log.error(`Error while rejecting an applicant`, error);
    }
  };

  // Wrap each async function with the hook.
  const { execute: rejectApplicant, loading: loadingReject } = useAsyncAction({
    asyncFn: rejectApplicantFunc,
  });

  return {
    rejectApplicant,
    loadingReject,
  };
};

export default useVacancyApplicantWorkflow;

type DeclineTemplateProps = {
  application: VacancyApplication;
  rentalUnitName: string;
  rentalUnitAddress: string;
};

const DeclineTemplate = (props: DeclineTemplateProps) => {
  const { application, rentalUnitAddress, rentalUnitName } = props;
  const applicant = application.data.applicants.find((a) => a.isMain);

  const saluationText = VacancyOfferUtils.getSalutationText(applicant);

  return (
    <div>
      <p>{saluationText},</p>
      <p>
        vielen Dank für Ihr Interesse an der Wohnung{" "}
        <strong>"{rentalUnitName}"</strong> in{" "}
        <strong>{rentalUnitAddress}</strong> und die Übersendung Ihrer
        Unterlagen. Nach sorgfältiger Prüfung haben wir uns für einen anderen
        Bewerber entschieden. Wir danken Ihnen herzlich für Ihre Mühe und das
        uns entgegengebrachte Vertrauen. Selbstverständlich werden alle von
        Ihnen übermittelten personenbezogenen Daten datenschutzkonform gelöscht.
        Für Ihre weitere Zukunft und Ihre Wohnungssuche wünschen wir Ihnen alles
        Gute und viel Erfolg.
      </p>
    </div>
  );
};

const getDeclineEmailBody = (props: DeclineTemplateProps) => {
  return ReactDOMServer.renderToString(
    <>
      <DeclineTemplate {...props} /> <VacancyMailSignature />
    </>
  );
};
